import { AutoAwesomeOutlined } from "@mui/icons-material";
import { Box, Link, Paper, Typography } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocalStorage } from "usehooks-ts";
import { useLocation } from "react-router-dom";
import { grey } from "../../../colors";
import MarkdownRenderer from "../../common/MarkdownRenderer";
import VerbatimFeedbackModal from "../../cards/VerbatimFeedbackModal";
import { ChatResponse } from "../models";
import {
  FocusedThreadQuestion,
  QuestionCanAcceptFeedback,
  SessionThreadAtom,
  ThinkingStepsAtom,
  ThinkingStepsCollapsedAtom,
} from "../state/DeepchatState";
import { scrollQuestionIntoView } from "../../../utils/scrollQuestionIntoView";
import SupportingDocuments from "./SupportingDocuments";
import ThinkingSteps from "../../common/ThinkingSteps";
import ClarifyingQuestions from "./ClarifyingQuestions";
import {
  useClarification,
  CLARIFICATION_CONTEXT_MARKER,
  parseClarificationValues,
} from "../../../store/home/useClarification";

export interface SessionResponseItemProps {
  response: ChatResponse;
  anchorId: string;
  index: number;
}

export default function SessionResponseItem({ response, anchorId, index }: SessionResponseItemProps) {
  const location = useLocation();
  const [focusedQuestion, setFocusedQuestion] = useAtom(FocusedThreadQuestion);
  const sessionThread = useAtomValue(SessionThreadAtom);
  const allowFeedback = useAtomValue(QuestionCanAcceptFeedback);
  const liveThinkingSteps = useAtomValue(ThinkingStepsAtom);
  const [liveCollapsed, setLiveCollapsed] = useAtom(ThinkingStepsCollapsedAtom);
  const { submitAnswers, skipClarification, selectEnhancedPrompt } = useClarification();
  const [, setClarificationEnabled] = useLocalStorage("deepchat:clarificationEnabled", true);

  const skipAndTurnOff = useCallback(() => {
    setClarificationEnabled(false);
    skipClarification();
  }, [setClarificationEnabled, skipClarification]);

  // The active (streaming) response is the last one before feedback is allowed.
  const isActiveResponse = index === sessionThread.length - 1 && !allowFeedback;

  // Live atoms for the active response, persisted data for historical ones.
  const thinkingSteps = isActiveResponse ? liveThinkingSteps : (response.thinkingSteps ?? []);

  // While the active response is still streaming, show only the question + thinking steps.
  const isAwaitingContent = isActiveResponse && !response.response && !response.clarification;
  const isCollapsed = liveCollapsed;
  const handleToggleCollapse = () => {
    if (isActiveResponse) {
      setLiveCollapsed((prev) => !prev);
    }
  };

  const { ref, inView } = useInView({ threshold: 0.8 });

  useEffect(() => {
    if (inView && focusedQuestion !== response) {
      setFocusedQuestion(response);
    }
  }, [inView]);

  // The stored query may carry appended clarification context; split it out as a subtitle.
  const [displayQuery, clarificationContext] = (() => {
    const raw = response.user_query ?? "";
    const idx = raw.indexOf(CLARIFICATION_CONTEXT_MARKER);
    if (idx === -1) return [raw, ""];
    const question = raw.slice(0, idx).trim();
    const context = raw.slice(idx + CLARIFICATION_CONTEXT_MARKER.length).trim();
    return [question, context];
  })();

  return (
    <Box className="session-response" sx={{ marginBottom: "4em" }}>
      <Typography variant="h5" color={"#13294B"} mb={clarificationContext ? "0.4em" : "1em"}>
        <Link
          href={`${location.pathname}#${anchorId}`}
          id={anchorId}
          underline="none"
          onClick={(event) => {
            event.preventDefault();
            scrollQuestionIntoView(anchorId);
          }}
        >
          {displayQuery}
        </Link>
      </Typography>
      {clarificationContext && (
        <Typography variant="body2" color={grey[500]} mb="1em" sx={{ fontStyle: "italic" }}>
          Clarifications — {clarificationContext.replace(/\.$/, "")}
        </Typography>
      )}

      {thinkingSteps.length > 0 && (
        <ThinkingStepsPanel
          steps={thinkingSteps}
          collapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
          isActive={isActiveResponse}
        />
      )}

      {/* The clarification module replaces the answer until the user answers, picks, or skips. */}
      {response.clarification && !response.response ? (
        <Box ref={ref}>
          <ClarifyingQuestions
            // Key on the session id to force a fresh mount per thread, since the list is index-keyed.
            key={String(response.user_chat_session)}
            clarification={response.clarification}
            sessionId={response.user_chat_session}
            // Gate on the turn being answered/historical, NOT isActiveResponse: the backend sends
            // COMPLETE right after CLARIFY, which would otherwise disable the inputs immediately.
            answered={Boolean(response.clarificationAnswered) || index !== sessionThread.length - 1}
            // On a reopened thread there is no localStorage draft, so reconstruct the original
            // selection from the follow-up turn's appended clarification context.
            prefillValues={parseClarificationValues(
              sessionThread[index + 1]?.user_query,
              response.clarification.questions?.[0]?.question ?? "",
            )}
            onSubmit={submitAnswers}
            onSkip={skipClarification}
            onSkipAndTurnOff={skipAndTurnOff}
            onSelectEnhancedPrompt={selectEnhancedPrompt}
          />
        </Box>
      ) : isAwaitingContent ? null : (
        <>
          <Box ref={ref}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="1em">
              <Box>
                <AutoAwesomeOutlined sx={{ fill: "#13294B", verticalAlign: "sub", marginRight: "0.5em" }} />
                <Typography variant="h6" color={"#13294B"} display="inline-block">
                  Deepchat Answer
                </Typography>
              </Box>
            </Box>
            <Paper sx={{ p: "1.5em", borderRadius: "1em" }}>
              <MarkdownRenderer text={response?.response || ""} />
            </Paper>
          </Box>

          <Box mt="2em">
            <SupportingDocuments sources={response.sources} />
          </Box>
        </>
      )}

      <VerbatimFeedbackModal />
    </Box>
  );
}

function ThinkingStepsPanel({
  steps,
  collapsed,
  onToggleCollapse,
  isActive,
}: {
  steps: ChatResponse["thinkingSteps"] & NonNullable<unknown>;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isActive: boolean;
}) {
  const [localCollapsed, setLocalCollapsed] = useState(collapsed);
  const [wasActive, setWasActive] = useState(isActive);

  // When transitioning from active to historical, capture the user's last collapsed choice
  if (wasActive && !isActive) {
    setWasActive(false);
    setLocalCollapsed(collapsed);
  }

  if (isActive) {
    if (!wasActive) setWasActive(true);
    return <ThinkingSteps steps={steps} collapsed={collapsed} onToggleCollapse={onToggleCollapse} isActive />;
  }

  return (
    <ThinkingSteps
      steps={steps}
      collapsed={localCollapsed}
      onToggleCollapse={() => setLocalCollapsed((prev) => !prev)}
    />
  );
}
